<template>
  <div class="consumer-search">
    <div class="search-bar">
      <input
        type="text"
        placeholder="검색어를 입력해주세요."
        v-model="searchKeyword"
        @keyup.enter="handleSearch"
      />
      <button @click="handleSearch">
        <span class="material-icons-outlined"> search </span>
      </button>
    </div>
    <h2>
      "{{
        route.query.keyword ? decodeURIComponent(route.query.keyword) : ""
      }}"에 대한 검색 결과: {{ productDatas.length.toLocaleString() }}건
    </h2>
    <div class="product-list-box">
      <div class="filter-container">
        <div class="view-filter-container">
          <div class="view-container">
            <div>
              <input
                type="radio"
                name="viewFilter"
                id="list"
                value="list"
                style="display: none"
                v-model="viewFilterData"
              />
              <label for="list">
                <span
                  class="material-icons-outlined"
                  :style="`color: ${
                    viewFilterData == 'list' ? '#007bff' : '#000000'
                  }`"
                >
                  format_list_bulleted
                </span>
              </label>
            </div>
            <div>
              <input
                type="radio"
                name="viewFilter"
                id="grid"
                value="grid"
                style="display: none"
                v-model="viewFilterData"
              />
              <label for="grid">
                <span
                  class="material-icons-outlined"
                  :style="`color: ${
                    viewFilterData == 'grid' ? '#007bff' : '#000000'
                  }`"
                >
                  grid_view
                </span>
              </label>
            </div>
          </div>
          <div class="pagenation-container"></div>
        </div>
      </div>
      <div
        class="product-list-container"
        :class="viewFilterData"
        v-if="productDatas.length > 0"
      >
        <div class="product" v-for="(item, index) in productDatas" :key="index">
          <button @click="handleClickProduct(item)">
            <img :src="item.productThumbnailUrl.originalUrl" />
          </button>
          <div class="desc-container">
            <div class="name-container">
              <p :style="`background-color: ${index < 3 ? '#007bff' : '#aaa'}`">
                {{ index + 1 }}
              </p>
              <button @click="handleClickProduct(item)">
                {{ item.productName }}
              </button>
            </div>
            <div class="price-container">
              <div class="sell-price-container">
                <button
                  class="sell-price"
                  v-if="visiblePriceCode(item, userData) === 0"
                >
                  로그인 후 가격 확인
                </button>
                <button
                  class="sell-price"
                  v-else-if="visiblePriceCode(item, userData) === 1"
                >
                  {{ item.productBankSellPrice.toLocaleString() }}원 ({{
                    item.productSellPrice.toLocaleString()
                  }}원)
                </button>
                <button class="sell-price" v-else>오프라인 전용 상품</button>
              </div>
            </div>
            <div class="footer-container">
              <p>
                등록월
                <span>{{ formatTimestampToYearMonth(item.createdAt) }}</span>
              </p>
              <hr v-if="viewFilterData == 'list'" />
              <p>
                관심
                <span>{{ item.productLikeCount.toLocaleString() }}</span>
              </p>
              <hr v-if="viewFilterData == 'list'" />
              <hr v-if="viewFilterData == 'grid'" />
              <p>
                조회수
                <span>{{ item.productViewCount.toLocaleString() }}</span>
              </p>
            </div>
          </div>
        </div>

        <div ref="observerTarget" class="observer-target"></div>

        <div v-if="isEnd" class="end-text">
          <p>더 이상 상품이 없습니다.</p>
        </div>
      </div>
      <div class="empty-container" v-else>
        <span class="material-icons-outlined"> error_outline </span>
        <p>검색된 상품이 없습니다.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="js">
import { onMounted, onUnmounted, ref, watch, nextTick } from "vue";
import { db } from "@/lib/firebase";
import {
  getDocs,
  query,
  collection,
  where,
  orderBy,
  limit,
  startAfter,
  getDoc,
  doc,
} from "firebase/firestore";
import { useRoute, useRouter } from "vue-router";
import { visiblePriceCode } from "@/lib/utils";
import { getUserId } from "@/lib/auth";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const productDatas = ref([]);
const orderFilterData = ref("popular");
const viewFilterData = ref("list");
const userData = ref(null);

const lastDoc = ref(null);
const isLoading = ref(false);
const isEnd = ref(false);

const PAGE_LIMIT = 30;

const observerTarget = ref(null);
let observer = null;

const route = useRoute();
const router = useRouter();

const handleClickProduct = (item) => {
  switch (visiblePriceCode(item, userData.value)) {
    case 0:
      alert("로그인 후 이용 가능합니다!");
      router.push("/login");
      break;
    case 1:
      router.push(`/product?id=${item.productId}`);
      break;
    case 2:
      alert("오프라인 전용 상품입니다!\n카카오톡이나 전화로 문의해주세요!");
      break;
  }
};

function formatTimestampToYearMonth(timestamp) {
  const date = timestamp?.toDate?.();
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}.${month}`;
}

function getOrderByByFilter(filter) {
  switch (filter) {
    case "popular":
      return ["popularScore", "desc"];
    case "ascPrice":
      return ["productSellPrice", "asc"];
    case "descPrice":
      return ["productSellPrice", "desc"];
    case "newest":
      return ["createdAt", "desc"];
    case "descView":
      return ["productViewCount", "desc"];
    case "descLike":
      return ["productLikeCount", "desc"];
    case "descPurchase":
      return ["purchaseCount", "desc"];
    default:
      return ["popularScore", "desc"];
  }
}

// --- 검색어 정규화 함수 분리 (재사용 + 가독성)
function getKeywordList(keywordQuery) {
  return String(keywordQuery || "")
    .toLowerCase()
    .replace(/\[|\]/g, "") // --- [ ] 제거
    .split(/\s+/)
    .map((kw) => kw.trim())
    .filter(Boolean)
    .slice(0, 10);
}

// --- 유저 정보는 BusyLock 사용하지 않음 (등급 체크용)
async function fetchUserData() {
  try {
    const userId = getUserId();

    // --- 비로그인 상태
    if (!userId) {
      userData.value = null;
      return;
    }

    const snap = await getDoc(doc(db, "users", String(userId)));
    userData.value = snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error("유저 정보 조회 실패:", error);
    userData.value = null;
  }
}

async function fetchProducts({ reset = false } = {}) {
  if (isLoading.value) return;

  if (reset) {
    productDatas.value = [];
    lastDoc.value = null;
    isEnd.value = false;
  }

  if (isEnd.value) return;

  const keywordList = getKeywordList(route.query.keyword);

  // --- 검색어가 없으면 쿼리 불가(array-contains-any 빈 배열 방지)
  if (!keywordList.length) {
    productDatas.value = [];
    isEnd.value = true;
    return;
  }

  isLoading.value = true;

  try {
    const [field, dir] = getOrderByByFilter(route.query.filter || "popular");

    let q = query(
      collection(db, "product"),
      where("isActive", "==", true),
      where("productNameKeywords", "array-contains-any", keywordList),
      orderBy(field, dir),
      limit(PAGE_LIMIT)
    );

    if (lastDoc.value) {
      q = query(q, startAfter(lastDoc.value));
    }

    const snap = await getDocs(q);

    if (snap.empty) {
      isEnd.value = true;
      return;
    }

    const rows = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));

    productDatas.value.push(...rows);
    lastDoc.value = snap.docs[snap.docs.length - 1];

    // --- 마지막 페이지 판단
    if (snap.size < PAGE_LIMIT) {
      isEnd.value = true;
    }
  } catch (e) {
    console.error("상품 검색 조회 실패:", e);
  } finally {
    isLoading.value = false;
  }
}

function initObserver() {
  if (observer) observer.disconnect();

  observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry?.isIntersecting) return;
      if (isLoading.value || isEnd.value) return;

      // --- 무한스크롤은 isLoading으로 제어 (runLocked 불필요)
      fetchProducts();
    },
    { rootMargin: "200px" } // --- 미리 로딩
  );

  if (observerTarget.value) {
    observer.observe(observerTarget.value);
  }
}

// --- 정렬 UI 변경 -> route query 반영만 수행
watch(orderFilterData, (val) => {
  if ((route.query.filter || "popular") === val) return;

  router.push({
    query: {
      ...route.query,
      filter: val,
    },
  });
});

// --- keyword/filter 변경 시 목록 재조회 (runLocked 적용)
watch(
  () => [route.query.keyword, route.query.filter],
  () => {
    runLocked(async () => {
      orderFilterData.value = route.query.filter || "popular";

      if (observer) observer.disconnect();

      await fetchProducts({ reset: true });

      await nextTick();
      initObserver();
    }).catch((error) => {
      console.error("검색 목록 갱신 실패:", error);
    });
  },
  { immediate: true }
);

onMounted(async () => {
  try {
    // --- userData는 runLocked 사용 금지 (등급 체크/가격 노출에 즉시 필요)
    await fetchUserData();

    // --- 목록은 watcher(immediate)에서 이미 처리함
    initObserver();
  } catch (error) {
    console.error("초기 로딩 실패:", error);
  }
});

onUnmounted(() => {
  if (observer) observer.disconnect();
});
</script>

<style lang="scss" scoped>
.observer-target {
  height: 1px;
}

.end-text {
  text-align: center;
  padding: 24px;
  color: #999;
}

.consumer-search {
  padding: 16px 24px;

  > h2 {
    margin-top: 24px;
    margin-bottom: 16px;
  }

  > .search-bar {
    padding: 8px 16px;
    width: 100%;
    border-radius: 100rem;
    border: 1px solid black;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 44px;

    > input {
      flex: 1;
      height: 100%;
      font-size: 16px;
      border: none;

      &:focus {
        outline: none;
      }
    }

    > button {
      background: none;
      border: none;
      cursor: pointer;
    }
  }

  > .product-list-box {
    margin-top: 8px;
    > .filter-container {
      display: flex;
      align-items: center;
      justify-content: end;
      gap: 24px;
      border-bottom: 1px solid #000;
      padding: 8px 0;

      label {
        cursor: pointer;
      }

      > .view-filter-container {
        display: flex;
        align-items: center;
        gap: 8px;

        > .view-container {
          display: flex;
          > div {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            border: 1px solid #ccc;
            > label {
              width: 24px;
              height: 24px;
            }
          }
        }
      }
    }

    > .product-list-container {
      margin-top: 8px;
      &.list {
        > .product {
          display: flex;
          gap: 16px;

          &:not(:last-child) {
            border-bottom: 1px solid #efefef;
            margin-top: 4px;
          }

          > button {
            border: none;
            outline: none;
            background: none;
            cursor: pointer;

            > img {
              width: 96px;
              height: 96px;
              border-radius: 8px;
              object-fit: cover;
              border: 1px solid #efefef;
              padding: 4px;
            }
          }

          > .desc-container {
            flex: 1;
            align-self: center;
            > .name-container {
              display: flex;
              gap: 8px;
              > p {
                color: white;
                font-size: 12px;
                min-width: 24px;
                max-width: 24px;
                height: 24px;
                text-align: center;
                line-height: 24px;
                font-weight: 700;
              }

              > button {
                border: none;
                outline: none;
                background: none;
                cursor: pointer;
                font-weight: 500;
                font-size: 14px;
              }
            }

            > .price-container {
              align-self: center;
              > .origin-price {
                color: #999;
                font-size: 14px;
                font-weight: 500;
                text-decoration: line-through;
                text-align: end;
              }

              > .sell-price-container {
                display: flex;
                flex-direction: column;
                text-align: start;
                margin-top: 8px;
                > .sell-price {
                  border: none;
                  outline: none;
                  background: none;
                  cursor: pointer;
                  text-align: start;
                  font-size: 16px;
                  font-weight: 700;
                  margin-top: 2px;

                  > span {
                    font-size: 14px;
                    color: #007bff;
                    margin-right: 2px;
                  }
                }
              }

              > .fee-price {
                font-size: 14px;
                font-weight: 500;
                color: #666;
                text-align: end;
                margin-top: 8px;
              }
            }

            > .footer-container {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-top: 12px;
              > p,
              button {
                color: #999;
                font-size: 12px;
                font-weight: 500;

                > span {
                  color: #007bff;
                }
              }
              > hr {
                width: 1px;
                height: 12px;
              }
            }
          }
        }
      }

      &.grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;
        > .product {
          padding: 8px;
          border: 1px solid #efefef;

          > button {
            border: none;
            outline: none;
            background: none;
            cursor: pointer;
            > img {
              width: 100%;
              aspect-ratio: 1 / 1;
              border-radius: 8px;
              object-fit: cover;
              border: 1px solid #efefef;
              padding: 4px;
            }
          }

          > .desc-container {
            margin-top: 8px;
            > .name-container {
              display: flex;
              gap: 8px;
              > p {
                color: white;
                font-size: 12px;
                min-width: 24px !important;
                width: 24px !important;
                height: 24px !important;
                text-align: center;
                line-height: 24px;
                font-weight: 700;
              }

              > button {
                border: none;
                outline: none;
                background: none;
                cursor: pointer;
                text-align: start;
                font-weight: 500;
                font-size: 14px;
              }
            }

            > .description-container {
              margin-top: 16px;
              > p {
                font-size: 14px;
                font-weight: 500;
                word-break: keep-all;
              }
            }

            > .footer-container {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-top: 12px;
              > p,
              button {
                color: #999;
                font-size: 12px;
                font-weight: 500;

                > span {
                  color: #007bff;
                }
              }
              > hr {
                width: 1px;
                height: 12px;
              }
            }

            > .price-container {
              margin-top: 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;

              > .origin-price {
                display: none;
              }

              > .sell-price-container {
                display: flex;
                flex-direction: column;
                width: 100%;
                > .sell-price {
                  border: none;
                  outline: none;
                  background: none;
                  cursor: pointer;
                  font-size: 16px;
                  font-weight: 700;
                  margin-top: 2px;
                }
              }
            }
          }
        }
      }
    }

    > .empty-container {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 16px;
      border-bottom: 1px solid #efefef;
      height: 100%;
      padding: 24px 0;

      > span {
        font-size: 96px;
        color: #999;
      }

      > p {
        color: #999;
        font-size: 18px;
        font-weight: 700;
      }
    }
  }
}
</style>
